/**
 * Login-password change form.
 *
 * Shared by the settings dialog and the onboarding wizard. The current
 * password is required so an unlocked session cannot lock the owner out;
 * every other session is signed out on success, the caller's stays.
 */
import { useState, type FormEvent } from 'react';
import { authApi, type ChangePasswordResult } from '../lib/api/auth.js';
import { ApiError } from '../lib/api/client.js';

type Status = { kind: 'idle' } | { kind: 'ok' | 'error'; message: string };

interface PasswordChangeFormProps {
  /**
   * First-run mode: no current password exists yet, so the form asks for the
   * new password only and calls POST /auth/setup (which signs the caller in).
   */
  setup?: boolean;
  /** Optional copy between the title and the fields (onboarding explains the seed). */
  description?: React.ReactNode;
  /** Client-side length hint; the server enforces its own minimum regardless. */
  minLength?: number;
  submitLabel?: string;
  /** Reports busy state so a modal can block Escape/backdrop close. */
  onBusyChange?: (busy: boolean) => void;
  onChanged: (result: ChangePasswordResult) => void;
}

export function PasswordChangeForm({
  setup = false,
  description,
  minLength = 1,
  submitLabel,
  onBusyChange,
  onChanged,
}: PasswordChangeFormProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [changing, setChanging] = useState(false);

  const tooShort = newPassword.length > 0 && newPassword.length < minLength;
  const actionLabel = submitLabel ?? (setup ? 'Set password' : 'Change password');

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (changing) return;

    if (newPassword !== confirmPassword) {
      setStatus({ kind: 'error', message: 'The new passwords do not match.' });
      return;
    }
    if (newPassword.length < minLength) {
      setStatus({
        kind: 'error',
        message: `The password must be at least ${minLength} characters.`,
      });
      return;
    }

    setChanging(true);
    onBusyChange?.(true);
    setStatus({ kind: 'idle' });
    try {
      const result = setup
        ? await authApi.setupPassword(newPassword)
        : await authApi.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setStatus({ kind: 'ok', message: setup ? 'Password set.' : 'Password updated.' });
      onChanged(result);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 403) {
        setStatus({ kind: 'error', message: 'The current password is incorrect.' });
      } else if (caught instanceof ApiError && caught.status === 409) {
        setStatus({
          kind: 'error',
          message: 'A password has already been set. Sign in and change it in Settings.',
        });
      } else if (caught instanceof ApiError && caught.status === 400) {
        setStatus({ kind: 'error', message: caught.message });
      } else {
        setStatus({
          kind: 'error',
          message: caught instanceof ApiError ? caught.message : 'Could not set the password.',
        });
      }
    } finally {
      setChanging(false);
      onBusyChange?.(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="storage-form">
      {description && <p className="modal-text">{description}</p>}

      {!setup && (
        <div className="settings-grid">
          <label className="settings-field">
            <span className="settings-label">Current password</span>
            <input
              className="input"
              type="password"
              value={currentPassword}
              autoComplete="current-password"
              disabled={changing}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </label>

          <label className="settings-field">
            <span className="settings-label">New password</span>
            <input
              className="input"
              type="password"
              value={newPassword}
              autoComplete="new-password"
              disabled={changing}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </label>
        </div>
      )}

      {setup && (
        <div className="settings-grid">
          <label className="settings-field">
            <span className="settings-label">New password</span>
            <input
              className="input"
              type="password"
              value={newPassword}
              autoComplete="new-password"
              disabled={changing}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </label>

          <label className="settings-field">
            <span className="settings-label">Confirm new password</span>
            <input
              className="input"
              type="password"
              value={confirmPassword}
              autoComplete="new-password"
              disabled={changing}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </label>
        </div>
      )}

      {!setup && (
        <label className="settings-field">
          <span className="settings-label">Confirm new password</span>
          <input
            className="input"
            type="password"
            value={confirmPassword}
            autoComplete="new-password"
            disabled={changing}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
        </label>
      )}

      {status.kind !== 'idle' && (
        <div
          className={
            status.kind === 'ok'
              ? 'settings-status settings-status-ok'
              : 'settings-status settings-status-error'
          }
          role="status"
        >
          {status.message}
        </div>
      )}

      <div className="modal-actions">
        <button
          type="submit"
          className="button"
          disabled={
            changing ||
            !newPassword ||
            !confirmPassword ||
            tooShort ||
            (!setup && !currentPassword)
          }
        >
          {changing ? 'Saving…' : actionLabel}
        </button>
      </div>
    </form>
  );
}
